import { useCachedFetch } from '@structural-climbing/auth'
import type { Discipline } from '@structural-climbing/validation'

export type ProgressPeriod = 'week' | 'month' | 'quarter'
export type ProgressDiscipline = 'all' | Discipline

export interface Hardest {
  boulder: number | null
  lead: number | null
}

export interface PeriodSummary {
  tops: number
  flashes: number
  points: number
  logs: number
  visits: number
  minutes: number
  hardest: Hardest
}

export interface SeriesBar {
  tops: number
  points: number
  minutes: number
}

export interface GradeRow {
  grade: number
  current: number
  previous: number
}

export interface MyProgress {
  period: ProgressPeriod
  discipline: ProgressDiscipline
  currentStart: string
  bucketDays: number
  current: PeriodSummary
  previous: PeriodSummary
  series: { current: SeriesBar; previous: SeriesBar }[]
  byGrade: { boulder?: GradeRow[]; lead?: GradeRow[] }
  allTime: Omit<PeriodSummary, 'logs'> & { firstAscents: number }
  streakWeeks: number
}

/** The member's own progress at a gym, for a window and the one before it. */
export function useMyProgress(
  gymId: string | null | undefined,
  apiUrl: string,
  getAccessTokenSilently: () => Promise<string>,
  discipline: ProgressDiscipline,
  period: ProgressPeriod,
) {
  const { data, loading, error } = useCachedFetch<MyProgress>(
    gymId ? `my-progress:${gymId}:${discipline}:${period}` : null,
    async () => {
      const token = await getAccessTokenSilently()
      const params = new URLSearchParams({ gym_id: gymId!, discipline, period })
      const res = await fetch(`${apiUrl}/my-progress?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load progress')
      return (await res.json()) as MyProgress
    },
  )
  return { data: data ?? null, loading, error: Boolean(error) }
}
